import { mdToHtml } from './markdown';
import {
    demoteHeadings,
    flattenInsights,
    hasInsights,
    insightHeading,
    normalizeSeverity,
    stripViewFor,
    summarizeEntries,
} from './libby_insights_view';

// Libby inline insights: a predictable on-page strip below the dashboard tab
// strip that replaces the old floating pop-up panel. The strip summarizes the
// structured issues the controller already computes; the full LLM answer is
// only shown in a Bootstrap modal.

const STRIP_ID = "libby-insights";
const MODAL_ID = "libby-insights-modal";
const UNAVAILABLE_ANSWERS = new Set(["AI summary unavailable.", "Summary unavailable."]);

const SEVERITY_ICON = {
    good: "fa-check-circle",
    info: "fa-info-circle",
    warn: "fa-exclamation-triangle",
    bad: "fa-exclamation-circle",
};
const SEVERITY_LABEL = { good: "Good", info: "Info", warn: "Watch", bad: "Attention" };

function normalizeLanguage(raw) {
    if (!raw) return "en-us";
    return String(raw).toLowerCase();
}

function getPreferredLanguage() {
    const preferred = (navigator.languages && navigator.languages.length > 0)
        ? navigator.languages[0]
        : (navigator.language || "en-us");
    return normalizeLanguage(preferred);
}

function getLocalTimeContext() {
    const now = new Date();
    return {
        local_time: now.toLocaleString(),
        local_time_iso: now.toISOString(),
        timezone_offset_minutes: -now.getTimezoneOffset(),
    };
}

function makeRequestId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `req_${Date.now()}_${Math.floor(Math.random() * 1e9)}`;
}

function normalizeExplainAnswer(answer) {
    if (!answer) return "";
    return String(answer)
        .replace(/\\r\\n/g, "\n")
        .replace(/\\n/g, "\n")
        .replace(/\\r/g, "\r");
}

function getExplainableDashlets(dashboard) {
    if (!dashboard || !Array.isArray(dashboard.dashlets)) return [];
    return dashboard.dashlets.filter((d) => typeof d.canExplain === "function" && d.canExplain());
}

function areDashletsReady(dashlets) {
    return dashlets.every((d) => typeof d.isExplainReady === "function" && d.isExplainReady());
}

function normalizeIssueWindow(window) {
    if (!window || typeof window !== "object") return null;
    const start = window.start ? String(window.start) : null;
    const end = window.end ? String(window.end) : null;
    if (!start && !end) return null;
    return { start, end };
}

function pushGroup(group, groups, legacyIssues) {
    if (!group || !group.region || !Array.isArray(group.issues)) return;
    const region = String(group.region);
    const normalizedIssues = group.issues
        .filter((entry) => entry && entry.issue)
        .map((entry) => ({
            time_range: entry.time_range ? String(entry.time_range) : null,
            severity: entry.severity ? String(entry.severity) : "info",
            issue: String(entry.issue),
            details: entry.details ? String(entry.details) : null,
            // Optional machine-readable hints added by newer dashlets. They
            // are forwarded unchanged so the backend can group deterministically;
            // older dashlets simply omit them.
            metric: entry.metric ? String(entry.metric) : null,
            kind: entry.kind ? String(entry.kind) : null,
            direction: entry.direction ? String(entry.direction) : null,
            node: entry.node ? String(entry.node) : null,
            node_kind: entry.node_kind ? String(entry.node_kind) : null,
            window: normalizeIssueWindow(entry.window),
        }));
    if (normalizedIssues.length === 0) return;
    groups.push({ region, issues: normalizedIssues });
    normalizedIssues.forEach((entry) => {
        const parts = [];
        if (entry.severity) parts.push(entry.severity);
        if (entry.time_range) parts.push(entry.time_range);
        parts.push(entry.issue);
        if (entry.details) parts.push(`(${entry.details})`);
        legacyIssues.push({
            dashlet_name: region,
            description: parts.join(": "),
        });
    });
}

function collectIssues(dashlets) {
    const issues = [];
    const groups = [];
    dashlets.forEach((d) => {
        if (typeof d.explain !== "function") return;
        const issue = d.explain();
        if (!issue) return;
        if (Array.isArray(issue)) {
            issue.forEach((group) => pushGroup(group, groups, issues));
            return;
        }
        if (issue.region && Array.isArray(issue.issues)) {
            pushGroup(issue, groups, issues);
            return;
        }
        if (issue.dashlet_name && issue.description) {
            issues.push({
                dashlet_name: String(issue.dashlet_name),
                description: String(issue.description),
            });
        }
    });
    return { issues, groups };
}

function dashboardStripAnchor() {
    return document.getElementById("dashboard_tabs")
        || document.querySelector("#dashboard .nav-tabs");
}

function ensureStrip() {
    let strip = document.getElementById(STRIP_ID);
    if (strip && strip.isConnected) return strip;

    strip = document.createElement("section");
    strip.id = STRIP_ID;
    strip.className = "lqos-strip libby-insights";
    strip.dataset.state = "idle";
    strip.dataset.severity = "info";
    strip.setAttribute("role", "region");
    strip.setAttribute("aria-label", "Libby insights");
    strip.setAttribute("aria-live", "polite");
    strip.innerHTML = `
        <div class="libby-insights-head">
            <img class="libby-insights-avatar" src="/libby.png" alt="" aria-hidden="true">
            <span class="libby-insights-name">Libby</span>
            <span class="lqos-strip-badge" data-role="badge"></span>
            <span class="lqos-strip-status" data-role="status"></span>
            <span class="lqos-strip-text" data-role="summary"></span>
            <button type="button" class="btn btn-sm lqos-strip-action lqos-strip-action--run" data-role="action" aria-disabled="false">More details</button>
        </div>
    `;

    const anchor = dashboardStripAnchor();
    if (anchor && anchor.parentNode) {
        anchor.insertAdjacentElement("afterend", strip);
    } else {
        const dashboard = document.getElementById("dashboard");
        if (dashboard) {
            dashboard.insertBefore(strip, dashboard.firstChild);
        } else {
            document.body.appendChild(strip);
        }
    }

    // One delegated handler covers both the action button (click bubbles) and
    // clicking anywhere on the strip body. The region itself stays a
    // role="region"/aria-live container, not a button.
    strip.addEventListener("click", () => runStripAction(strip));
    return strip;
}

function runStripAction(strip) {
    try {
        const view = strip._libbyView || idleView();
        if (view.state === "loading") return;
        if (view.state === "ready") {
            openModal(strip);
            return;
        }
        const controller = strip._libbyController;
        if (controller && typeof controller.requestExplain === "function") {
            controller.requestExplain({ force: true });
        }
    } catch (error) {
        // A click on the strip must never break the page.
    }
}

function applyBadge(badgeEl, severity, label, iconClass) {
    const icon = iconClass || SEVERITY_ICON[severity] || SEVERITY_ICON.info;
    badgeEl.className = "lqos-strip-badge";
    badgeEl.innerHTML = `<i class="fas ${icon}" aria-hidden="true"></i><span>${label}</span>`;
}

function buildIssueRow(entry) {
    const li = document.createElement("li");
    li.className = "libby-insights-issue";

    const chip = document.createElement("span");
    chip.className = `libby-insights-chip is-${entry.severity}`;
    chip.innerHTML = `<i class="fas ${SEVERITY_ICON[entry.severity] || SEVERITY_ICON.info}" aria-hidden="true"></i><span>${SEVERITY_LABEL[entry.severity] || "Info"}</span>`;
    li.appendChild(chip);

    const text = document.createElement("span");
    text.className = "libby-insights-issue-text";
    text.textContent = entry.region ? `${entry.region}: ${entry.issue}` : entry.issue;
    li.appendChild(text);

    if (entry.time_range) {
        const time = document.createElement("span");
        time.className = "libby-insights-time";
        time.textContent = entry.time_range;
        li.appendChild(time);
    }

    if (entry.details) {
        const details = document.createElement("span");
        details.className = "libby-insights-details";
        details.textContent = String(entry.details);
        li.appendChild(details);
    }
    return li;
}

function applyView(strip, view) {
    strip.dataset.state = view.state;
    strip.dataset.severity = view.severity;
    strip._libbyView = view;

    applyBadge(
        strip.querySelector('[data-role="badge"]'),
        view.severity,
        view.badgeLabel,
        view.badgeIcon,
    );
    strip.querySelector('[data-role="status"]').textContent = view.status;

    // Single inline row: the issues are summarised as one truncated line (full
    // detail lives in the "More details" modal). No per-issue chips/timestamps.
    const summaryEl = strip.querySelector('[data-role="summary"]');
    summaryEl.textContent = (view.issues || [])
        .map((entry) => (typeof entry === "string"
            ? entry
            : (entry.region ? `${entry.region}: ${entry.issue}` : entry.issue)))
        .join("  ·  ");

    const action = view.actionKind || "run";
    const actionBtn = strip.querySelector('[data-role="action"]');
    actionBtn.hidden = action === "none";
    if (action !== "none") {
        actionBtn.textContent = view.actionLabel || "More details";
        actionBtn.className = action === "run"
            ? "btn btn-sm lqos-strip-action lqos-strip-action--run"
            : "btn btn-sm lqos-strip-action";
        // Keep the control focusable while a review is in flight: a `disabled`
        // element loses focus, so use aria-disabled and let runStripAction's
        // loading guard make activation a no-op.
        actionBtn.setAttribute("aria-disabled", action === "loading" ? "true" : "false");
    }
}

function idleView() {
    return {
        state: "idle",
        severity: "info",
        badgeLabel: "Waiting",
        badgeIcon: "fa-robot",
        status: "Waiting for dashboard data…",
        issues: [],
        entries: [],
        answer: "",
        insights: null,
        actionKind: "none",
        actionLabel: "",
    };
}

function loadingView() {
    return {
        state: "loading",
        severity: "info",
        badgeLabel: "Reviewing",
        badgeIcon: "fa-spinner fa-spin",
        status: "Libby is reviewing this dashboard…",
        issues: [],
        entries: [],
        answer: "",
        insights: null,
        actionKind: "loading",
        actionLabel: "Reviewing…",
    };
}

function unavailableView(message) {
    return {
        state: "unavailable",
        severity: "warn",
        badgeLabel: "Unavailable",
        badgeIcon: "fa-exclamation-triangle",
        status: message || "Libby's review isn't available right now.",
        issues: [],
        entries: [],
        answer: "",
        insights: null,
        actionKind: "run",
        actionLabel: "Retry",
    };
}

function readyView(entries, answer, insights = null, headline = "") {
    // A new backend sends a deterministic insight list. Prefer it for the
    // at-a-glance strip: headline plus the top three labels, no click needed.
    // An empty insights array is not "all good" — it falls back to the local
    // structured summary, which knows about warn entries the backend omitted.
    const summary = stripViewFor(entries, insights, headline);
    const fromInsights = summary.source === "insights";
    return {
        state: "ready",
        severity: summary.severity,
        badgeLabel: SEVERITY_LABEL[summary.severity] || "Info",
        badgeIcon: SEVERITY_ICON[summary.severity] || SEVERITY_ICON.info,
        status: summary.status,
        issues: summary.labels,
        entries,
        answer: answer || "",
        insights: fromInsights ? insights.slice() : null,
        actionKind: "open",
        actionLabel: "More details",
    };
}

function modalAnswerHtml(answer) {
    const md = normalizeExplainAnswer(answer || "").trim();
    if (md && !UNAVAILABLE_ANSWERS.has(md)) return demoteHeadings(mdToHtml(md));
    return "";
}

function insightMetaParts(insight) {
    const parts = [];
    if (insight.direction === "upload") parts.push("Upload");
    else if (insight.direction === "download") parts.push("Download");
    else if (insight.direction === "both") parts.push("Both directions");
    const windowLabel = insight.window && insight.window.label
        ? String(insight.window.label)
        : "";
    if (windowLabel) parts.push(windowLabel);
    const affected = insight.affected;
    if (affected && affected.count) {
        const kind = String(affected.kind || "items");
        const noun = affected.count === 1 ? kind.replace(/s$/i, "") : kind;
        parts.push(`${affected.count} ${noun}`);
    }
    return parts;
}

function buildInsightCard(insight) {
    const severity = normalizeSeverity(insight.severity);
    const card = document.createElement("article");
    card.className = "libby-insight-card";

    const head = document.createElement("div");
    head.className = "libby-insight-card-head";
    const chip = document.createElement("span");
    chip.className = `libby-insights-chip is-${severity}`;
    chip.innerHTML = `<i class="fas ${SEVERITY_ICON[severity] || SEVERITY_ICON.info}" aria-hidden="true"></i><span>${SEVERITY_LABEL[severity] || "Info"}</span>`;
    head.appendChild(chip);

    // A real heading lets screen-reader users move insight-by-insight. Include
    // the direction so repeated labels (upload vs download) stay distinct.
    const label = document.createElement("h6");
    label.className = "libby-insight-label";
    label.textContent = insightHeading(insight);
    head.appendChild(label);

    const metaParts = insightMetaParts(insight);
    if (metaParts.length > 0) {
        const meta = document.createElement("span");
        meta.className = "libby-insight-meta";
        meta.textContent = metaParts.join(" · ");
        head.appendChild(meta);
    }
    card.appendChild(head);

    if (insight.summary) {
        const summary = document.createElement("p");
        summary.className = "libby-insight-summary";
        summary.textContent = String(insight.summary);
        card.appendChild(summary);
    }
    if (insight.detail) {
        const detail = document.createElement("p");
        detail.className = "libby-insight-detail text-body-secondary";
        detail.textContent = String(insight.detail);
        card.appendChild(detail);
    }
    return card;
}

function buildEvidenceBlock(insights) {
    const wrapper = document.createElement("div");
    wrapper.className = "libby-insights-evidence";
    (insights || []).forEach((insight) => {
        const evidence = Array.isArray(insight.evidence) ? insight.evidence : [];
        if (evidence.length === 0) return;
        const details = document.createElement("details");
        details.className = "libby-insight-evidence";
        const summary = document.createElement("summary");
        // The summary is the only control that expands the evidence, so make
        // it reachable by keyboard (Enter/Space activate it).
        summary.setAttribute("tabindex", "0");
        summary.textContent = `Evidence for ${insightHeading(insight)} (${evidence.length})`;
        details.appendChild(summary);
        const ul = document.createElement("ul");
        ul.className = "libby-insights-issues libby-insights-issues-modal";
        evidence.forEach((entry) => {
            ul.appendChild(buildIssueRow({
                region: entry.region ? String(entry.region) : "",
                issue: entry.issue ? String(entry.issue) : "",
                time_range: entry.time_range ? String(entry.time_range) : null,
                details: entry.details ? String(entry.details) : null,
                severity: normalizeSeverity(entry.severity),
            }));
        });
        details.appendChild(ul);
        wrapper.appendChild(details);
    });
    return wrapper;
}

function renderInsightsBody(body, view) {
    const insights = Array.isArray(view.insights) ? view.insights : [];
    if (insights.length === 0) {
        const empty = document.createElement("p");
        empty.className = "text-body-secondary mb-3";
        empty.textContent = "Everything looks good.";
        body.appendChild(empty);
    } else {
        const list = document.createElement("div");
        list.className = "libby-insight-list";
        insights.forEach((insight) => list.appendChild(buildInsightCard(insight || {})));
        body.appendChild(list);
    }

    const answerHtml = modalAnswerHtml(view.answer);
    if (answerHtml) {
        const heading = document.createElement("h6");
        heading.className = "libby-insights-section-title";
        heading.textContent = "What Libby says";
        body.appendChild(heading);
        const narrative = document.createElement("div");
        narrative.className = "libby-insights-narrative";
        narrative.innerHTML = answerHtml;
        body.appendChild(narrative);
    }

    const evidence = buildEvidenceBlock(insights);
    if (evidence.childElementCount > 0) {
        const heading = document.createElement("h6");
        heading.className = "libby-insights-section-title";
        heading.textContent = "Evidence";
        body.appendChild(heading);
        body.appendChild(evidence);
    }
}

function renderModalBody(modal, view) {
    const body = modal.querySelector('[data-role="modal-body"]');
    body.innerHTML = "";

    // New backend: deterministic insight cards + optional narrative + grouped,
    // collapsible evidence. Per-bucket rows stay hidden until expanded. An
    // empty array is not an insight payload: use the local fallback below.
    if (hasInsights(view.insights)) {
        renderInsightsBody(body, view);
        return;
    }

    const answerHtml = modalAnswerHtml(view.answer);
    if (answerHtml) {
        body.innerHTML = answerHtml;
        return;
    }
    // No LLM answer and no structured insights (e.g. training, or an old
    // backend): fall back to the compact local list so the modal is still useful.
    body.innerHTML = "<p class=\"text-body-secondary\">Libby's detailed written explanation isn't available right now. Here is the full summary of what was reviewed.</p>";
    const entries = view.entries || [];
    if (entries.length === 0) {
        const p = document.createElement("p");
        p.className = "text-body-secondary mb-0";
        p.textContent = "No items were flagged.";
        body.appendChild(p);
        return;
    }
    const ul = document.createElement("ul");
    ul.className = "libby-insights-issues libby-insights-issues-modal";
    entries.forEach((entry) => ul.appendChild(buildIssueRow(entry)));
    body.appendChild(ul);
}

function ensureModal() {
    let modal = document.getElementById(MODAL_ID);
    if (modal && modal.isConnected) return modal;

    modal = document.createElement("div");
    modal.id = MODAL_ID;
    modal.className = "modal fade";
    modal.tabIndex = -1;
    modal.setAttribute("aria-hidden", "true");
    modal.setAttribute("aria-labelledby", `${MODAL_ID}-title`);
    modal.innerHTML = `
        <div class="modal-dialog modal-lg modal-dialog-scrollable">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title" id="${MODAL_ID}-title">
                        <img class="libby-insights-avatar me-2" src="/libby.png" alt="" aria-hidden="true">Libby — full explanation
                    </h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body libby-insights-modal-body" data-role="modal-body"></div>
            </div>
        </div>
    `;
    document.body.appendChild(modal);
    return modal;
}

function openModal(strip) {
    const view = strip._libbyView || idleView();
    const modal = ensureModal();
    renderModalBody(modal, view);

    if (!modal._libbyFocusReturnBound) {
        modal._libbyFocusReturnBound = true;
        modal.addEventListener("hidden.bs.modal", () => {
            const trigger = strip.querySelector('[data-role="action"]');
            if (trigger && typeof trigger.focus === "function") trigger.focus();
        });
    }
    window.bootstrap.Modal.getOrCreateInstance(modal, { keyboard: true }).show();
}

export function createExplainController({
    dashboard,
    page,
    getContext,
    sendOverride,
    getExplainable,
    expandExplainScope,
    collapseExplainScope,
    readyGraceMs = 4000,
    forceGraceMs = 12000,
} = {}) {
    const state = {
        pending: false,
        inFlight: false,
        lastRequestId: null,
        timeoutId: null,
        graceId: null,
        // Explicit requests wait longer and give an honest answer when panels
        // are still loading; automatic requests stay quiet instead.
        forced: false,
        // Structured data captured at send time so the strip can render it.
        groups: [],
        issues: [],
        view: idleView(),
    };
    let api = null;

    function explainableDashlets() {
        if (typeof getExplainable === "function") {
            return getExplainable() || [];
        }
        return getExplainableDashlets(dashboard);
    }

    function renderView(view) {
        state.view = view;
        const strip = ensureStrip();
        // ensureStrip() may have re-created the element (e.g. after an editor
        // rebuild); always re-bind the controller so the action stays live.
        strip._libbyController = api;
        applyView(strip, view);
    }

    function syncStrip() {
        // Re-attach the strip if a dashboard rebuild removed it.
        const strip = ensureStrip();
        strip._libbyController = api;
    }

    // Render Libby's high-level status from the structured data the dashlets
    // already expose, without waiting for (or paying for) an LLM round-trip.
    function refreshStructured() {
        const dashlets = explainableDashlets().filter((d) => (
            typeof d.isExplainReady === "function" ? d.isExplainReady() : true
        ));
        if (dashlets.length === 0) return false;
        const { issues, groups } = collectIssues(dashlets);
        const entries = flattenInsights(groups, issues);
        if (entries.length === 0) return false;
        state.groups = groups;
        state.issues = issues;
        // Structured-only pass: there is no remembered LLM answer on state, so
        // the modal uses the structured list until handleExplainResult arrives.
        renderView(readyView(entries, ""));
        return true;
    }

    function clearGrace() {
        if (state.graceId) {
            clearTimeout(state.graceId);
            state.graceId = null;
        }
    }

    function clearInFlightTimeout() {
        if (state.timeoutId) {
            clearTimeout(state.timeoutId);
            state.timeoutId = null;
        }
    }

    function schedulePartialExplain() {
        if (state.graceId) return;
        const graceMs = state.forced ? forceGraceMs : readyGraceMs;
        state.graceId = setTimeout(() => {
            state.graceId = null;
            if (!state.pending || state.inFlight) return;
            sendExplain({ allowPartial: true });
        }, graceMs);
    }

    function markNeedsExplain() {
        state.pending = true;
        state.forced = false;
        syncStrip();
        // Show the current structured status immediately; the LLM answer (if
        // any) is fetched separately for the "More details" modal.
        refreshStructured();
    }

    function requestExplain({ force = false } = {}) {
        if (force && typeof expandExplainScope === "function") {
            expandExplainScope();
        }
        if (force) {
            renderView(loadingView());
        } else {
            syncStrip();
        }
        state.pending = true;
        state.forced = force;
        if (state.inFlight && !force) {
            return;
        }
        trySend();
    }

    function trySend() {
        const dashlets = explainableDashlets();
        if (dashlets.length === 0) return;
        if (areDashletsReady(dashlets)) {
            sendExplain();
        } else {
            schedulePartialExplain();
        }
    }

    function sendExplain({ allowPartial = false } = {}) {
        const dashlets = explainableDashlets();
        if (dashlets.length === 0) return;
        if (!allowPartial && !areDashletsReady(dashlets)) return;

        const { issues, groups } = collectIssues(dashlets);
        state.groups = groups;
        state.issues = issues;
        const entries = flattenInsights(groups, issues);

        // We only reach a partial send when a dashlet never became ready.
        // Do not present that incomplete read as "all healthy".
        if (allowPartial && entries.length === 0) {
            state.pending = false;
            clearGrace();
            if (state.forced) {
                renderView(unavailableView("Some dashboard panels were still loading, so Libby could not complete a full summary. Everything available looks normal."));
            } else {
                renderView(idleView());
            }
            return;
        }

        const requestId = makeRequestId();
        state.lastRequestId = requestId;
        state.inFlight = true;
        state.pending = false;
        clearGrace();
        clearInFlightTimeout();
        state.timeoutId = setTimeout(() => {
            state.inFlight = false;
        }, 30000);

        const context = (typeof getContext === "function") ? getContext() : {};
        const timeContext = getLocalTimeContext();
        const payload = {
            page: String(page || "unknown"),
            node_id: context.node_id ? String(context.node_id) : "",
            site_id: context.site_id ? String(context.site_id) : null,
            circuit_id: context.circuit_id ? String(context.circuit_id) : null,
            language: getPreferredLanguage(),
            issues,
            issues_v2: groups.length > 0 ? groups : null,
            local_time: timeContext.local_time,
            local_time_iso: timeContext.local_time_iso,
            timezone_offset_minutes: timeContext.timezone_offset_minutes,
            request_id: requestId
        };

        const sender = sendOverride || (window.ws && window.ws.send ? window.ws.send.bind(window.ws) : null);
        if (sender) {
            sender(["ExplainDashboard", payload]);
            return;
        }

        // No transport (e.g. the training preview): render the structured
        // summary locally so the strip is still useful without a chatbot.
        handleExplainResult({
            request_id: requestId,
            severity: summarizeEntries(entries).severity,
            answer: "",
        });
    }

    function maybeExplain() {
        if (!state.pending || state.inFlight) return;
        trySend();
    }

    function handleExplainResult(result) {
        if (!result) return;
        if (result.request_id && state.lastRequestId && result.request_id !== state.lastRequestId) {
            return;
        }
        state.inFlight = false;
        clearGrace();
        clearInFlightTimeout();
        if (typeof collapseExplainScope === "function") {
            collapseExplainScope();
        }
        const insights = Array.isArray(result.insights) ? result.insights : null;
        const headline = result.headline ? String(result.headline) : "";
        const entries = flattenInsights(state.groups, state.issues);
        renderView(readyView(entries, result.answer || "", insights, headline));
        if (state.pending) {
            maybeExplain();
        }
    }

    api = { markNeedsExplain, maybeExplain, handleExplainResult, requestExplain };
    const strip = ensureStrip();
    strip._libbyController = api;
    applyView(strip, state.view);
    return api;}

// Adds the top-right "Explain" button that requests a review. Shared by the
// live and training entry scripts. `beforeRequest` runs on click before the
// review is requested (the live pages use it to arm auto-explain).
export function setupLibbyExplainButton(controller, { beforeRequest } = {}) {
    const controls = document.getElementById("controls");
    if (!controls || document.getElementById("libbyExplainBtn")) return null;

    const btn = document.createElement("button");
    btn.type = "button";
    btn.id = "libbyExplainBtn";
    btn.className = "btn btn-outline-secondary btn-sm";
    btn.innerHTML = `<img src="/libby.png" alt="Libby" style="width:16px;height:16px;margin-right:4px;"> Explain`;
    btn.addEventListener("click", () => {
        if (typeof beforeRequest === "function") beforeRequest();
        if (controller && typeof controller.requestExplain === "function") {
            controller.requestExplain({ force: true });
        }
    });
    controls.appendChild(btn);
    return btn;
}
